import React, { FC, useEffect, useState } from "react";
import styles from "./styles.module.scss"; // SCSS styles

interface InputFieldProps {
	label: string;
	name: string;
	type?: React.HTMLInputTypeAttribute | "select";
	placeholder?: string;
	value: string | number;
	onChange: (
		name: string,
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
	) => void;
	error?: string;
	autoComplete?: React.HTMLInputAutoCompleteAttribute;
	secondary?: boolean;
	isRequired?: boolean;
	disabled?: boolean;
	selectOptions?: { value: string; label: string | number | boolean }[];
	tooltip?: string;
}

const InputField: FC<InputFieldProps> = ({
	label,
	type = "text",
	name,
	placeholder,
	value,
	autoComplete,
	onChange,
	error,
	secondary,
	selectOptions,
	isRequired = false,
	disabled = false,
	tooltip,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [select, setSelect] = useState(selectOptions || []);

	useEffect(() => {
		if (selectOptions) setSelect(selectOptions);
	}, [selectOptions]);

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
	) => {
		onChange(name, e);
	};

	return (
		<div className={styles.inputField}>
			<label
				htmlFor={name}
				className={`${styles.label} ${secondary && styles.secondary}`}
			>
				{label}
				{isRequired && <span>*</span>}

				{tooltip && (
					<span className={styles.tooltipWrapper}>
						<button
							type="button"
							className={styles.tooltipButton}
							aria-label="Tooltip"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="14"
								height="14"
								fill="currentColor"
								viewBox="0 0 16 16"
							>
								<circle cx="8" cy="8" r="8" fill="#007bff" />
								<text
									x="8"
									y="11"
									fontSize="10"
									fontFamily="Arial, sans-serif"
									fill="white"
									textAnchor="middle"
									fontWeight="bold"
								>
									i
								</text>
							</svg>
						</button>

						<span className={styles.tooltipText}>{tooltip}</span>
					</span>
				)}
			</label>

			<div className={styles.inputWrapper}>
				{type === "select" ? (
					<select
						id={name}
						name={name}
						required={isRequired}
						value={value}
						disabled={disabled}
						onChange={handleInputChange}
						className={`${styles.input} ${secondary && styles.secondary} ${
							error ? styles.errorInput : ""
						}`}
					>
						<option value="" disabled>
							{placeholder || "Select an option"}
						</option>
						{select.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				) : (
					<input
						id={name}
						required={isRequired}
						autoComplete={autoComplete}
						name={name}
						type={type === "password" && showPassword ? "text" : type}
						value={value}
						disabled={disabled}
						placeholder={placeholder}
						onChange={handleInputChange}
						className={`${styles.input} ${secondary && styles.secondary} ${
							error ? styles.errorInput : ""
						}`}
					/>
				)}

				{type === "password" && (
					<span
						className={styles.icon}
						onClick={() => setShowPassword((prev) => !prev)}
					>
						{showPassword ? (
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M7.00065 3.48587C9.21148 3.48587 11.1832 4.72837 12.1457 6.69421C11.8015 7.40587 11.3173 8.01837 10.7398 8.51421L11.5623 9.33671C12.3732 8.61921 13.0148 7.72087 13.4173 6.69421C12.4082 4.13337 9.91732 2.31921 7.00065 2.31921C6.25982 2.31921 5.54815 2.43587 4.87732 2.65171L5.83982 3.61421C6.21899 3.53837 6.60398 3.48587 7.00065 3.48587ZM6.37648 4.15087L7.58398 5.35837C7.91648 5.50421 8.18482 5.77254 8.33065 6.10504L9.53815 7.31254C9.58482 7.11421 9.61982 6.90421 9.61982 6.68837C9.62565 5.24171 8.44732 4.06921 7.00065 4.06921C6.78482 4.06921 6.58065 4.09837 6.37648 4.15087ZM1.17315 2.24337L2.73648 3.80671C1.78565 4.55337 1.03315 5.54504 0.583984 6.69421C1.59315 9.25504 4.08398 11.0692 7.00065 11.0692C7.88732 11.0692 8.73899 10.9 9.52065 10.5909L11.5157 12.5859L12.3382 11.7634L1.99565 1.41504L1.17315 2.24337ZM5.54815 6.61837L7.07065 8.14087C7.04732 8.14671 7.02398 8.15254 7.00065 8.15254C6.19565 8.15254 5.54232 7.49921 5.54232 6.69421C5.54232 6.66504 5.54815 6.64754 5.54815 6.61837ZM3.56482 4.63504L4.58565 5.65587C4.45148 5.97671 4.37565 6.32671 4.37565 6.69421C4.37565 8.14087 5.55398 9.31921 7.00065 9.31921C7.36815 9.31921 7.71815 9.24337 8.03315 9.10921L8.60482 9.68087C8.09149 9.82087 7.55482 9.90254 7.00065 9.90254C4.78982 9.90254 2.81815 8.66004 1.85565 6.69421C2.26398 5.86004 2.85898 5.17171 3.56482 4.63504Z"
									fill="black"
									fillOpacity="0.45"
								/>
							</svg>
						) : (
							<svg
								width="14"
								height="14"
								viewBox="0 0 32 32"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M4.27024 16.8325C4.03297 16.4971 3.8393 16.2057 3.69148 15.9754C3.82647 15.7491 4.00292 15.4648 4.21993 15.1383C4.79918 14.267 5.65923 13.1077 6.78125 11.9529C9.04436 9.62363 12.2386 7.44444 16.25 7.44444C20.251 7.44444 23.3149 9.6136 25.4459 11.9287C26.504 13.0783 27.3016 14.2328 27.8344 15.1011C28.0459 15.4458 28.2145 15.7433 28.3398 15.9747C28.2017 16.2099 28.0159 16.5142 27.7839 16.8677C27.2106 17.7413 26.3638 18.9026 25.2699 20.0589C23.0552 22.4 19.9895 24.5556 16.25 24.5556C12.4992 24.5556 9.30424 22.3891 6.9575 20.0357C5.79969 18.8745 4.89022 17.709 4.27024 16.8325ZM30.8895 15.4975C30.8897 15.498 30.8899 15.4984 29.75 16L30.8676 16.5474L30.8671 16.5485L30.8656 16.5513L30.8614 16.5596L30.8473 16.5865C30.8354 16.6091 30.8186 16.6407 30.797 16.6808C30.7537 16.7609 30.6909 16.8749 30.6089 17.0179C30.4449 17.3038 30.2037 17.7068 29.888 18.1878C29.2582 19.1476 28.3237 20.4307 27.1051 21.7188C24.6948 24.2667 21.0105 27 16.25 27C11.5008 27 7.69576 24.2776 5.1675 21.7421C3.88781 20.4588 2.89103 19.1799 2.21414 18.223C1.87491 17.7435 1.61399 17.3419 1.43614 17.0572C1.34717 16.9147 1.27884 16.8013 1.23177 16.7217C1.20823 16.6819 1.18999 16.6505 1.17713 16.6282L1.16187 16.6016L1.15729 16.5935L1.15473 16.589L2.25 16C1.13197 15.4534 1.13218 15.453 1.13242 15.4525L1.13298 15.4514L1.13447 15.4485L1.13883 15.4401L1.15311 15.4128C1.16511 15.39 1.18204 15.3581 1.20388 15.3178C1.24757 15.2372 1.31094 15.1228 1.39378 14.9792C1.55937 14.6923 1.80331 14.2883 2.12382 13.8061C2.76332 12.8441 3.71577 11.5589 4.96875 10.2693C7.45563 7.70971 11.2614 5 16.25 5C21.249 5 24.9351 7.71973 27.3041 10.2935C28.496 11.5883 29.3859 12.8783 29.9781 13.8434C30.275 14.3272 30.4991 14.7327 30.6507 15.0209C30.7265 15.1651 30.7843 15.2802 30.8242 15.3613C30.8441 15.4019 30.8595 15.434 30.8705 15.457L30.8835 15.4848L30.8876 15.4934L30.8889 15.4964L30.8895 15.4975ZM1.15473 16.589C0.956792 16.2371 0.948283 15.8126 1.13197 15.4534L2.25 16C1.16233 16.5849 1.15455 16.5886 1.15473 16.589L1.15473 16.589ZM30.8676 16.5474C31.0356 16.219 31.044 15.8333 30.8899 15.4984L29.75 16C30.868 16.5466 30.8678 16.547 30.8676 16.5474ZM19.75 16C19.75 17.89 18.183 19.4222 16.25 19.4222C14.317 19.4222 12.75 17.89 12.75 16C12.75 14.11 14.317 12.5778 16.25 12.5778C18.183 12.5778 19.75 14.11 19.75 16ZM22.25 16C22.25 19.2401 19.5637 21.8667 16.25 21.8667C12.9363 21.8667 10.25 19.2401 10.25 16C10.25 12.7599 12.9363 10.1333 16.25 10.1333C19.5637 10.1333 22.25 12.7599 22.25 16Z"
									fill="black"
									fillOpacity="0.45"
								/>
							</svg>
						)}
					</span>
				)}
			</div>

			{error && <p className={styles.errorMessage}>{error}</p>}
		</div>
	);
};

export default InputField;
